<template>
    <footer class="relative overflow-hidden bg-[#131317] text-white">
        <!-- BG -->
        <div class="absolute inset-0 z-0">
            <img
                src="@img/footer/footer_bg.png"
                alt="footer bg"
                class="h-full w-full object-cover"
            />
            <div class="absolute inset-0 bg-black/80"></div>
            <div class="absolute inset-0 bg-gradient-to-b from-black/40 via-black/55 to-black/85"></div>
        </div>

        <!-- glow -->
        <div class="pointer-events-none absolute inset-0 z-0 overflow-hidden">
            <div class="absolute left-[-60px] top-[40px] h-[220px] w-[220px] rounded-full bg-[#ffc400]/10 blur-3xl"></div>
            <div class="absolute right-[10%] top-[80px] h-[240px] w-[240px] rounded-full bg-white/5 blur-3xl"></div>
        </div>

        <!-- Content -->
        <div class="container relative z-10 py-14 md:py-16 lg:py-20">
            <!-- TOP BLOCK -->
            <div
                class="grid grid-cols-1 gap-8 border-b border-white/10 pb-10 md:grid-cols-2 md:gap-10 md:pb-12 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,0.9fr)]"
            >
                <div class="flex flex-col justify-between gap-6">
                    <div>
                        <div
                            class="mb-4 inline-flex rounded-full border border-white/10 bg-white/5 px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-white/70 shadow-sm"
                        >
                            Связаться с нами
                        </div>

                        <h2
                            class="max-w-[640px] text-2xl md:text-4xl font-extrabold leading-[1.05] tracking-[-0.04em] text-white"
                        >
                            Бесплатная консультация с бизнес-аналитиком
                        </h2>
                    </div>

                    <div>
                        <div @click.prevent="callbackStore.showBaseCallbackPopup({title: 'Получить консультацию', description: 'Получить консультацию'})"
                             class="inline-flex h-[52px] w-full items-center justify-center rounded-xl bg-baseColor px-6 text-[16px] font-bold text-black transition hover:bg-[#e6b000] md:w-auto md:min-w-[226px] cursor-pointer">
                            Получить консультацию
                        </div>
                    </div>
                </div>

                <div class="flex items-center ">
                    <div
                        class="relative rounded-[24px] border border-white/10 bg-white/5 p-6 shadow-sm md:p-7 overflow-hidden"
                    >
                        <div class="absolute left-0 top-0 h-full w-[3px] rounded-full bg-baseColor"></div>

                        <p class="pl-5 text-[15px] leading-[1.85] text-white/75 md:text-[17px]">
                            Внедрение высоких технологий, которые помогают оптимизировать
                            бизнес-процессы, сокращать операционные расходы и обеспечивать
                            прозрачность работы команды.
                        </p>
                    </div>
                </div>
            </div>

            <!-- CONTACT BLOCK -->
            <div class="grid grid-cols-1 gap-4 py-10 md:py-12 lg:grid-cols-4 lg:gap-5">
                <!-- LOGO -->
                <div
                    class="flex min-h-[92px] items-center justify-center rounded-[24px] border border-white/10 bg-white/5 px-5 shadow-sm md:px-6"
                >
                    <Link :href="route('home')" class="flex items-center justify-center">
                        <IconLogo class="h-10 w-auto" />
                    </Link>
                </div>

                <div
                    class="flex min-h-[92px] items-center gap-4 rounded-[24px] border border-white/10 bg-white/5 px-5 text-white/70 shadow-sm md:px-6"
                >
                    <div
                        class="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-white/15 bg-white/5"
                    >
                        <IconMapPoint class="h-5 w-5" />
                    </div>

                    <p class="text-sm leading-[1.6]">
                        Москва, пос. Совхоза имени Ленина, 17 корпус 2
                    </p>
                </div>

                <div
                    class="flex min-h-[92px] items-center gap-4 rounded-[24px] border border-white/10 bg-white/5 px-5 text-white/70 shadow-sm md:px-6"
                >
                    <div
                        class="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-white/15 bg-white/5"
                    >
                        <IconEnvelope class="h-5 w-5" />
                    </div>

                    <a
                        :href="'mailto:' + contacts.info_mail"
                        target="_blank"
                        class="text-sm transition hover:text-white"
                    >
                        {{ contacts.info_mail }}
                    </a>
                </div>

                <div
                    class="flex min-h-[92px] items-center gap-4 rounded-[24px] border border-white/10 bg-white/5 px-5 text-white/70 shadow-sm md:px-6"
                >
                    <div
                        class="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-white/15 bg-white/5"
                    >
                        <IconPhone class="h-5 w-5" />
                    </div>

                    <div class="text-sm transition hover:text-white">
                        <BasePhoneComponent />
                    </div>
                </div>
            </div>

            <!-- BOTTOM -->
            <div
                class="flex flex-col gap-4 border-t border-white/10 pt-6 md:flex-row md:items-center md:justify-between md:pt-8"
            >
                <p class="text-center text-sm text-white/55 md:text-left">
                    © {{ year }} «ИП ГОГОРЯН». ИНН 181703161790
                </p>

                <ul class="flex items-center justify-center gap-6 text-sm text-white/65 md:justify-end">
                    <li>
                        <Link
                            :href="route('privacyPolicy')"
                            class="transition hover:text-baseColor"
                        >
                            Политика конфиденциальности
                        </Link>
                    </li>
                </ul>
            </div>
        </div>
    </footer>
</template>

<script setup>
import IconLogo from "@js/components/Icons/IconLogo.vue";
import { useCallbackStore } from "@js/store/CallbackStore.js";
import { Link } from "@inertiajs/vue3";
import IconPhone from "@js/components/Icons/IconPhone.vue";
import IconEnvelope from "@js/components/Icons/IconEnvelope.vue";
import IconMapPoint from "@js/components/Icons/IconMapPoint.vue";
import BasePhoneComponent from "@js/components/BasePhoneComponent.vue";
import { contacts } from "@js/company_details.ts";

const year = new Date().getFullYear();
const callbackStore = useCallbackStore();
</script>
